import React, {useContext, useEffect, useMemo, useRef} from 'react';
import {createStackNavigator, TransitionPresets} from "@react-navigation/stack";
import {useTranslation} from "react-i18next";
import {Dimensions, Platform, Text, TouchableOpacity, View} from "react-native";
import {Ionicons} from '@expo/vector-icons';
import {useTopInset} from '../helpers/safeArea';
import {useTheme} from '../helpers/ThemeContext';
import ConnectivityIndicator from './ConnectivityIndicator';
import SyncBadge from './SyncBadge';
import {WarehouseDataProvider} from '../helpers/WarehouseDataContext';
import {TourContext} from '../helpers/TourContext';
import PrintQueueIndicator from './PrintQueueIndicator';
import lazyScreen from './lazyScreen';
import StartScreen from '../screens/StartScreen';
import HomeScreen from '../screens/HomeScreen';
import LoginScreen from '../screens/LoginScreen';
import DayOpeningScreen from '../screens/DayOpeningScreen';
import {getPosEntryRoute} from '../helpers/posEntryRoute';
import RepairmanHomeScreen from '../screens/RepairmanHomeScreen';
import ForgotPasswordScreen from '../screens/ForgotPasswordScreen';
import ManagementHomeScreen from '../screens/ManagementHomeScreen';
import {ManagementDataProvider} from '../helpers/ManagementDataContext';
import {hasNotificationLaunchIntent} from '../helpers/notificationLaunchIntent';
import {isAppModuleEnabled} from '../config/appVariant';
import MyDayBriefing from './management/MyDayBriefing';
import WorkdayHeaderPill from './workday/WorkdayHeaderPill';
import {withPosTheme} from './pos/PosThemeScope';

/**
 * Wrap the management navigator in the live-data provider only when the
 * management module is active, so background polling never runs for other app
 * modes (sales / service / logistics). When disabled it renders children as-is.
 * @param {{enabled: boolean, children: React.ReactNode}} props
 */
const MaybeManagementData = ({enabled, children}) => (
    enabled ? <ManagementDataProvider>{children}</ManagementDataProvider> : children
);

// Keep startup-critical screens eager to avoid blank initial renders after splash.
const loadNotificationsScreen = () => import('../screens/NotificationsScreen');
const loadOnlineScreen = () => import('../screens/OnlineScreen');
const loadOrderDetailsScreen = () => import('../screens/OrderDetailsScreen');
const loadTablesScreen = () => import('../screens/TablesScreen');
const loadSettingsScreen = () => import('../screens/SettingsScreen');
const loadOrdersScreen = () => import('../screens/OrdersScreen');
const NotificationDetailScreen = lazyScreen(() => import('../screens/NotificationDetailScreen'));
const AdminWebScreen = lazyScreen(() => import('../screens/AdminWebScreen'));
const NotificationsScreen = lazyScreen(loadNotificationsScreen);
const OnlineScreen = lazyScreen(loadOnlineScreen);
const OrderDetailsScreen = lazyScreen(loadOrderDetailsScreen);
const SettingsPrinterPairingScreen = lazyScreen(() => import('../screens/SettingsPrinterPairingScreen'));
const SuccessEntryScreen = lazyScreen(() => import('../screens/SuccessEntryScreen'));
const SuccessPaymentScreen = lazyScreen(() => import('../screens/SuccessPaymentScreen'));
const IncomingCallScreen = lazyScreen(() => import('../screens/IncomingCallScreen'));
const TablesScreen = lazyScreen(loadTablesScreen);
const PosHubScreen = lazyScreen(() => import('../screens/PosHubScreen'));
const TermsOfServicesScreen = lazyScreen(() => import('../screens/TermsOfServices'));
const VipUserScreen = lazyScreen(() => import('../screens/VipUserScreen'));
const VipUserDetailScreen = lazyScreen(() => import('../screens/VipUserDetailScreen'));
const PaymentScreen = lazyScreen(() => import('../screens/PaymentScreen'));
const ResultScreen = lazyScreen(() => import('../screens/ResultScreen'));
const WaitingForPayment = lazyScreen(() => import('../screens/WaitingForPayment'));
const FailedPaymentScreen = lazyScreen(() => import('../screens/FailedPaymentScreen'));
// POS selling screens: dark navy theme on phones (Settings › "Dark POS screens").
const PosHomeScreen = withPosTheme(HomeScreen);
const PosHubScreenThemed = withPosTheme(PosHubScreen);
const PosOnlineScreen = withPosTheme(OnlineScreen);
const PosTablesScreen = withPosTheme(TablesScreen);
const PosPaymentScreen = withPosTheme(PaymentScreen);
const TicketEntryScreen = lazyScreen(() => import('../screens/TicketEntryScreen'));
const FailedEntryScreen = lazyScreen(() => import('../screens/FailedEntryScreen'));
const PaymentsSettingsScreen = lazyScreen(() => import('../screens/PaymentsSettingsScreen'));
const CashierWifiSettingsScreen = lazyScreen(() => import('../screens/CashierWifiSettingsScreen'));
const ScanDevicesScreen = lazyScreen(() => import('../screens/ScanDevicesScreen'));
const PrivacyPolicyScreen = lazyScreen(() => import('../screens/PrivacyPolicyScreen'));
const DayClosingScreenExtended = lazyScreen(() => import('../screens/DayClosingScreenExtended'));
const DayClosingScreenBasic = lazyScreen(() => import('../screens/DayClosingScreenBasic'));
const DayOpeningConfirmationScreen = lazyScreen(() => import('../screens/DayOpeningConfirmationScreen'));
const DesignSettingsScreen = lazyScreen(() => import('../screens/DesignSettingsScreen'));
const SuccessDayClosingScreen = lazyScreen(() => import('../screens/SuccessDayClosingScreen'));
const SupportChatScreen = lazyScreen(() => import('../screens/SupportChatScreen'));
const GuestSupportChatScreen = lazyScreen(() => import('../screens/GuestSupportChatScreen'));
const SettingsPrinterSearchScreen = lazyScreen(() => import('../screens/SettingsPrinterSearchScreen'));
const StatisticsScreen = lazyScreen(() => import('../screens/StatisticsScreen'));
const OrderListScreen = lazyScreen(() => import('../screens/OrderListScreen'));
const AddVipUserScreen = lazyScreen(() => import('../screens/AddVipUserScreen'));
const SettingsScreen = lazyScreen(loadSettingsScreen);
const OrdersScreen = lazyScreen(loadOrdersScreen);
const TablesBookingsScreen = lazyScreen(() => import('../screens/TablesBookingsScreen'));
const OtherSettingsScreen = lazyScreen(() => import('../screens/OtherSettingsScreen'));
const ProfileEditScreen = lazyScreen(() => import('../screens/ProfileEditScreen'));
const ChangePasswordScreen = lazyScreen(() => import('../screens/ChangePasswordScreen'));
const TwoFactorScreen = lazyScreen(() => import('../screens/TwoFactorScreen'));
const NotificationSettingsScreen = lazyScreen(() => import('../screens/NotificationSettingsScreen'));
const MeetingRecordingSettingsScreen = lazyScreen(() => import('../screens/MeetingRecordingSettingsScreen'));
const MeetingRecorderScreen = lazyScreen(() => import('../screens/meetings/MeetingRecorderScreen'));
const MeetingDetailScreen = lazyScreen(() => import('../screens/meetings/MeetingDetailScreen'));
const AiAgentSettingsScreen = lazyScreen(() => import('../screens/AiAgentSettingsScreen'));
// "Ask anything" AI assistant chat — universal across every app mode (see AIAssistantScreen).
const AIAssistantScreen = lazyScreen(() => import('../screens/assistant/AIAssistantScreen'));
// Team Chat + meetings ("Csapatchat") — universal across every app mode: the
// header chat button (Management TopBar, POS CustomHeader), the Service "Egyéb"
// list, the Warehouse dashboard tile, the Sales "Egyéb" hub and chat push taps
// all land here, so it must never be gated behind a module flag.
const ChatHomeScreen = lazyScreen(() => import('../screens/chat/ChatHomeScreen'));
const ChatChannelScreen = lazyScreen(() => import('../screens/chat/ChatChannelScreen'));
const ChatThreadScreen = lazyScreen(() => import('../screens/chat/ChatThreadScreen'));
const ChatChannelInfoScreen = lazyScreen(() => import('../screens/chat/ChatChannelInfoScreen'));
const ChatNewScreen = lazyScreen(() => import('../screens/chat/ChatNewScreen'));
const ChatSettingsScreen = lazyScreen(() => import('../screens/chat/ChatSettingsScreen'));
const ChatModuleAppsScreen = lazyScreen(() => import('../screens/chat/ChatModuleAppsScreen'));
const ChatMeetingScreen = lazyScreen(() => import('../screens/chat/ChatMeetingScreen'));
const WidgetsScreen = lazyScreen(() => import('../screens/WidgetsScreen'));
const InsightWidgetSettingsScreen = lazyScreen(() => import('../screens/InsightWidgetSettingsScreen'));
const LoggedInDevicesScreen = lazyScreen(() => import('../screens/LoggedInDevicesScreen'));
const ProductTrafficScreen = lazyScreen(() => import('../screens/ProductTrafficScreen'));
const NovoTerminalSettingsScreen = lazyScreen(() => import('../screens/NovoTerminalSettingsScreen'));
const Sam4sSettingsScreen = lazyScreen(() => import('../screens/Sam4sCashierSettingsScreen'));
const ExpensesScreen = lazyScreen(() => import('../screens/ExpensesScreen'));
const BookingDetailsScreen = lazyScreen(() => import('../screens/BookingDetailsScreen'));
const OfflineSyncStatusScreen = lazyScreen(() => import('../screens/OfflineSyncStatusScreen'));
const HaccpScreen = lazyScreen(() => import('../screens/HaccpScreen'));
const FunctionsScreen = lazyScreen(() => import('../screens/FunctionsScreen'));
const PrinterProfilesScreen = lazyScreen(() => import('../screens/PrinterProfilesScreen'));
const PrinterProfileEditScreen = lazyScreen(() => import('../screens/PrinterProfileEditScreen'));
const ManagementListScreen = lazyScreen(() => import('../screens/ManagementListScreen'));
const ManagementDetailScreen = lazyScreen(() => import('../screens/ManagementDetailScreen'));
const ManagementQuoteEditorScreen = lazyScreen(() => import('../screens/ManagementQuoteEditorScreen'));
const ManagementStoreSettingsScreen = lazyScreen(() => import('../screens/ManagementStoreSettingsScreen'));
const ManagementBillingScreen = lazyScreen(() => import('../screens/ManagementBillingScreen'));
const ManagementUsersScreen = lazyScreen(() => import('../screens/ManagementUsersScreen'));
const ManagementRolesScreen = lazyScreen(() => import('../screens/ManagementRolesScreen'));
const ManagementModuleTemplatesScreen = lazyScreen(() => import('../screens/ManagementModuleTemplatesScreen'));
const ManagementServiceModesScreen = lazyScreen(() => import('../screens/ManagementServiceModesScreen'));
const CalendarSettingsScreen = lazyScreen(() => import('../screens/CalendarSettingsScreen'));
// Time tracker. Registered in the UNIVERSAL block below, not the management one:
// a technician in Service mode tracks work-order time too, and the home-screen
// widget's "+ New task" row deep-links here from whatever mode the app is in, so
// gating it behind `managementEnabled` would strand both.
const TimeTrackerScreen = lazyScreen(() => import('../screens/TimeTrackerScreen'));
// "Share to Happy" — the OS share-sheet target (components/share/ShareIntentHost
// routes every share here). Universal: chat, mail and attachments exist in every mode.
const ShareToHappyScreen = lazyScreen(() => import('../screens/share/ShareToHappyScreen'));
const ManagementCrmScreen = lazyScreen(() => import('../screens/ManagementCrmScreen'));
// Apollo.io prospecting (lead search + integration settings). Deliberately
// registered in the SHARED management block below rather than the
// management-only one: the CRM hub opens it in Management mode and the Sales
// "Egyéb" hub opens it in Sales mode, and a sales-only variant ships
// `modules.management: false` — registering it management-only would make the
// very first Apollo tap in that build throw the unhandled-NAVIGATE error.
const ApolloSearchScreen = lazyScreen(() => import('../screens/apollo/ApolloSearchScreen'));
const ApolloSettingsScreen = lazyScreen(() => import('../screens/apollo/ApolloSettingsScreen'));
const QuoteTemplatePickerScreen = lazyScreen(() => import('../screens/management/QuoteTemplatePickerScreen'));
const QuoteDocumentScreen = lazyScreen(() => import('../screens/management/QuoteDocumentScreen'));
const DocumentGalleryPickerScreen = lazyScreen(() => import('../screens/management/DocumentGalleryPickerScreen'));
const DocumentDocumentScreen = lazyScreen(() => import('../screens/management/DocumentDocumentScreen'));
// Procurement (Beszerzés). Registered in the UNIVERSAL block below rather than
// the management-only one on purpose: the POS "Funkciók" screen opens
// requisitions and orders straight from its Raktári műveletek section, and a
// till-only build ships `modules.management: false` — registering these
// management-only would make the very first tap there throw the
// unhandled-NAVIGATE error.
const ProcurementHomeScreen = lazyScreen(() => import('../screens/procurement/ProcurementHomeScreen'));
const ProcurementRequisitionsScreen = lazyScreen(() => import('../screens/procurement/ProcurementRequisitionsScreen'));
const ProcurementRequisitionDetailScreen = lazyScreen(() => import('../screens/procurement/ProcurementRequisitionDetailScreen'));
const ProcurementRfqsScreen = lazyScreen(() => import('../screens/procurement/ProcurementRfqsScreen'));
const ProcurementRfqDetailScreen = lazyScreen(() => import('../screens/procurement/ProcurementRfqDetailScreen'));
const ProcurementOrdersScreen = lazyScreen(() => import('../screens/procurement/ProcurementOrdersScreen'));
const ProcurementOrderDetailScreen = lazyScreen(() => import('../screens/procurement/ProcurementOrderDetailScreen'));
const ProcurementSourcingScreen = lazyScreen(() => import('../screens/procurement/ProcurementSourcingScreen'));
const ProcurementReorderRulesScreen = lazyScreen(() => import('../screens/procurement/ProcurementReorderRulesScreen'));
const ProcurementSettingsScreen = lazyScreen(() => import('../screens/procurement/ProcurementSettingsScreen'));
// Utility bills & meters (Rezsi nyilvántartás). UNIVERSAL block like Procurement:
// the module hub tile and its bottom bar reach every screen, and the field flow
// (meter reading with photos) must open from any app mode that exposes the hub.
const UtilityHomeScreen = lazyScreen(() => import('../screens/utilities/UtilityHomeScreen'));
const UtilityProvidersScreen = lazyScreen(() => import('../screens/utilities/UtilityProvidersScreen'));
const UtilityProviderDetailScreen = lazyScreen(() => import('../screens/utilities/UtilityProviderDetailScreen'));
const UtilityReadingScreen = lazyScreen(() => import('../screens/utilities/UtilityReadingScreen'));
const UtilityBillScreen = lazyScreen(() => import('../screens/utilities/UtilityBillScreen'));
const UtilityAnomaliesScreen = lazyScreen(() => import('../screens/utilities/UtilityAnomaliesScreen'));
// Fleet (Flotta) management screens — universal like Procurement/Utilities: reached from
// the logistics hub and the Modules hub, gated by `managementAccess` key `fleet`.
const ManagementFleetStatsScreen = lazyScreen(() => import('../screens/fleet/ManagementFleetStatsScreen'));
const ManagementRefuelsScreen = lazyScreen(() => import('../screens/fleet/ManagementRefuelsScreen'));
const ManagementDriverBreaksScreen = lazyScreen(() => import('../screens/fleet/ManagementDriverBreaksScreen'));
const ManagementLogisticsScreen = lazyScreen(() => import('../screens/ManagementLogisticsScreen'));
const ManagementAutomationsScreen = lazyScreen(() => import('../screens/ManagementAutomationsScreen'));
// Finance & Service left the bottom tab bar (5-tab layout: Dashboard / Modules /
// Mail / Calendar / Settings) and are reached from the Modules hub as stack routes.
const ManagementFinanceScreen = lazyScreen(() => import('../screens/ManagementFinanceScreen'));
const ManagementInvoiceEditorScreen = lazyScreen(() => import('../screens/ManagementInvoiceEditorScreen'));
const ManagementServiceScreen = lazyScreen(() => import('../screens/ManagementServiceScreen'));
const ManagementDeploymentsScreen = lazyScreen(() => import('../screens/ManagementDeploymentsScreen'));
const HappyAiInsightsScreen = lazyScreen(() => import('../screens/HappyAiInsightsScreen'));
const ManagementDeploymentDetailScreen = lazyScreen(() => import('../screens/ManagementDeploymentDetailScreen'));
const ManagementRuntimeLogsScreen = lazyScreen(() => import('../screens/ManagementRuntimeLogsScreen'));
const ManagementSystemStatusScreen = lazyScreen(() => import('../screens/ManagementSystemStatusScreen'));
// Per-user dashboard editor (widgets / KPIs / quick actions) behind the Manager
// dashboard hero's tune icon.
const ManagementDashboardCustomizeScreen = lazyScreen(() => import('../screens/ManagementDashboardCustomizeScreen'));

// Mail module screens (universal — available in every app mode).
const MailHomeScreen = lazyScreen(() => import('../screens/mail/MailHomeScreen'));
const MailThreadScreen = lazyScreen(() => import('../screens/mail/MailThreadScreen'));
const MailComposeScreen = lazyScreen(() => import('../screens/mail/MailComposeScreen'));
const MailSearchScreen = lazyScreen(() => import('../screens/mail/MailSearchScreen'));
const MailAccountWizardScreen = lazyScreen(() => import('../screens/mail/MailAccountWizardScreen'));
const MailSettingsScreen = lazyScreen(() => import('../screens/mail/MailSettingsScreen'));
const MailRulesScreen = lazyScreen(() => import('../screens/mail/MailRulesScreen'));
const MailRuleEditorScreen = lazyScreen(() => import('../screens/mail/MailRuleEditorScreen'));
const MailDesignTemplatesScreen = lazyScreen(() => import('../screens/mail/MailDesignTemplatesScreen'));
const MailTemplatesScreen = lazyScreen(() => import('../screens/mail/MailTemplatesScreen'));
const MailSecurityScreen = lazyScreen(() => import('../screens/mail/MailSecurityScreen'));
const MailSharedMailboxScreen = lazyScreen(() => import('../screens/mail/MailSharedMailboxScreen'));
const MailAccountShareScreen = lazyScreen(() => import('../screens/mail/MailAccountShareScreen'));
const MailScheduledScreen = lazyScreen(() => import('../screens/mail/MailScheduledScreen'));

// VoIP softphone screens (universal — available in every app mode).
const VoipSettingsScreen = lazyScreen(() => import('../screens/voip/VoipSettingsScreen'));
const DialerScreen = lazyScreen(() => import('../screens/voip/DialerScreen'));
const CallHistoryScreen = lazyScreen(() => import('../screens/voip/CallHistoryScreen'));
const DirectoryScreen = lazyScreen(() => import('../screens/voip/DirectoryScreen'));

// Repairman module screens
const WorkSheetListScreen = lazyScreen(() => import('../screens/WorkSheetListScreen'));
const WorkSheetDetailScreen = lazyScreen(() => import('../screens/WorkSheetDetailScreen'));
const RepairmanCalendarScreen = lazyScreen(() => import('../screens/RepairmanCalendarScreen'));
const RepairPhotoScreen = lazyScreen(() => import('../screens/RepairPhotoScreen'));
const CustomerVehicleScreen = lazyScreen(() => import('../screens/CustomerVehicleScreen'));
const PartsInventoryScreen = lazyScreen(() => import('../screens/PartsInventoryScreen'));

// Sales (CRM field-sales) module screens. Route NAMES are duplicated as string
// literals below exactly like every other screen here, but the vocabulary they
// must agree with lives in `config/salesRoutes.js` — change one, change both.
const SalesHomeScreen = lazyScreen(() => import('../screens/SalesHomeScreen'));
// Driver / courier (Sofor) app — premium screens, each draws its own header.
const DriverHomeScreen = lazyScreen(() => import('../screens/driver/DriverHomeScreen'));
const DriverRouteDetailScreen = lazyScreen(() => import('../screens/driver/DriverRouteDetailScreen'));
const DriverStopScreen = lazyScreen(() => import('../screens/driver/DriverStopScreen'));
const DriverOrderDetailScreen = lazyScreen(() => import('../screens/driver/DriverOrderDetailScreen'));
const DriverPartiesScreen = lazyScreen(() => import('../screens/driver/DriverPartiesScreen'));
const DriverPartyDetailScreen = lazyScreen(() => import('../screens/driver/DriverPartyDetailScreen'));
const DriverPartyCreateScreen = lazyScreen(() => import('../screens/driver/DriverPartyCreateScreen'));
const DriverChangeRequestsScreen = lazyScreen(() => import('../screens/driver/DriverChangeRequestsScreen'));
const DriverContactsScreen = lazyScreen(() => import('../screens/driver/DriverContactsScreen'));
const DriverBreaksScreen = lazyScreen(() => import('../screens/driver/DriverBreaksScreen'));
const DriverStatsScreen = lazyScreen(() => import('../screens/driver/DriverStatsScreen'));
const DriverWorkTimeScreen = lazyScreen(() => import('../screens/driver/DriverWorkTimeScreen'));
const SalesDashboardScreen = lazyScreen(() => import('../screens/sales/SalesDashboardScreen'));
const SalesOrdersScreen = lazyScreen(() => import('../screens/sales/SalesOrdersScreen'));
const SalesOrderBuilderScreen = lazyScreen(() => import('../screens/sales/SalesOrderBuilderScreen'));
const SalesOrderReviewScreen = lazyScreen(() => import('../screens/sales/SalesOrderReviewScreen'));
const SalesCustomerDetailScreen = lazyScreen(() => import('../screens/sales/SalesCustomerDetailScreen'));
const SalesFollowUpsScreen = lazyScreen(() => import('../screens/sales/SalesFollowUpsScreen'));

// Warehouseman module screens
const WarehousemanHomeScreen = lazyScreen(() => import('../screens/WarehousemanHomeScreen'));
const WarehouseReceivingListScreen = lazyScreen(() => import('../screens/WarehouseReceivingListScreen'));
const WarehouseReceivingScreen = lazyScreen(() => import('../screens/WarehouseReceivingScreen'));
const WarehouseReceivingPlacementScreen = lazyScreen(() => import('../screens/WarehouseReceivingPlacementScreen'));
const StockCountScreen = lazyScreen(() => import('../screens/StockCountScreen'));
const StockDraftsScreen = lazyScreen(() => import('../screens/StockDraftsScreen'));
const StockWriteOffScreen = lazyScreen(() => import('../screens/StockWriteOffScreen'));
const WarehousePickingListScreen = lazyScreen(() => import('../screens/WarehousePickingListScreen'));
const WarehousePickingScreen = lazyScreen(() => import('../screens/WarehousePickingScreen'));
const WarehousePackingScreen = lazyScreen(() => import('../screens/WarehousePackingScreen'));
const WarehousePackingCheckingScreen = lazyScreen(() => import('../screens/WarehousePackingCheckingScreen'));

const Stack = createStackNavigator();

const WarehouseReceivingScreenWithProvider = (props) => (
  <WarehouseDataProvider>
    <WarehouseReceivingScreen {...props} />
  </WarehouseDataProvider>
);

const StockCountScreenWithProvider = (props) => (
  <WarehouseDataProvider>
    <StockCountScreen {...props} />
  </WarehouseDataProvider>
);

const StockDraftsScreenWithProvider = (props) => (
  <WarehouseDataProvider>
    <StockDraftsScreen {...props} />
  </WarehouseDataProvider>
);

// Selejtezés / Visszáru — shared by the warehouseman, management, POS-mode and driver modules.
const StockWriteOffScreenWithProvider = (props) => (
  <WarehouseDataProvider>
    <StockWriteOffScreen {...props} />
  </WarehouseDataProvider>
);

const WarehousePackingCheckingScreenWithProvider = (props) => (
  <WarehouseDataProvider>
    <WarehousePackingCheckingScreen {...props} />
  </WarehouseDataProvider>
);
const HIDDEN_HEADER_ROUTES = new Set([
  'StartScreen',
  'HomeScreen',
  'LoginScreen',
  'OnlineScreen',
  'TablesScreen',
  'PosHubScreen',
  'OrderDetailsScreen',
  'SettingsPrinterPairingScreen',
  'PrinterProfilesScreen',
  'PrinterProfileEditScreen',
  'RepairmanHomeScreen',
  'WorkSheetDetailScreen',
  'WarehousemanHomeScreen',
  'ManagementHomeScreen',
  'ManagementDashboardCustomizeScreen',
  // Sales (CRM field-sales) screens render their own premium ManagementScreen
  // top bar / gradient hero, so a stack header would be a second one.
  'SalesHomeScreen',
  'DriverHomeScreen',
  'DriverRouteDetailScreen',
  'DriverStopScreen',
  'DriverOrderDetailScreen',
  'DriverPartiesScreen',
  'DriverPartyDetailScreen',
  'DriverPartyCreateScreen',
  'DriverChangeRequestsScreen',
  'DriverContactsScreen',
  'DriverBreaksScreen',
  'DriverStatsScreen',
  'DriverWorkTimeScreen',
  // Conversation-canvas chats draw their own translucent header.
  'SupportChatScreen',
  'GuestSupportChatScreen',
  'SalesDashboardScreen',
  'SalesOrdersScreen',
  'SalesOrderBuilderScreen',
  'SalesOrderReviewScreen',
  'SalesCustomerDetailScreen',
  'SalesFollowUpsScreen',
  // Apollo screens are ManagementScreen-wrapped (own TopBar + GradientHero), so
  // a stack header would be a second one — same reason as the Sales block above.
  'ApolloSearchScreen',
  'ApolloSettingsScreen',
  'CalendarSettingsScreen',
  // Settings screens render their own premium gradient header.
  'SettingsScreen',
  'ProfileEditScreen',
  'ChangePasswordScreen',
  'TwoFactorScreen',
  'NotificationSettingsScreen',
  'MeetingRecordingSettingsScreen',
  // Meeting screens render their own premium ManagementScreen top bar.
  'MeetingRecorderScreen',
  'MeetingDetailScreen',
  'AiAgentSettingsScreen',
  // The AI assistant chat renders its own premium ManagementScreen top bar.
  'AIAssistantScreen',
  // Team Chat screens render their own premium ManagementScreen top bar.
  'ChatHomeScreen',
  'ChatChannelScreen',
  'ChatThreadScreen',
  'ChatChannelInfoScreen',
  'ChatNewScreen',
  'ChatSettingsScreen',
  'ChatModuleAppsScreen',
  'ChatMeetingScreen',
  'TimeTrackerScreen',
  'ShareToHappyScreen',
  'WidgetsScreen',
  'InsightWidgetSettingsScreen',
  // The notification inbox and its detail page render their own premium gradient
  // header (the inbox already hid the stack header at runtime, costing a frame flash).
  'NotificationsScreen',
  'NotificationDetailScreen',
  // Authenticated in-app admin web page — renders its own premium header.
  'AdminWebScreen',
  'LoggedInDevicesScreen',
  // Mail screens render their own premium gradient/topbar headers.
  'MailHomeScreen',
  'MailThreadScreen',
  'MailComposeScreen',
  'MailSearchScreen',
  'MailAccountWizardScreen',
  'MailSettingsScreen',
  'MailRulesScreen',
  'MailRuleEditorScreen',
  'MailDesignTemplatesScreen',
  'MailTemplatesScreen',
  'MailSecurityScreen',
  'MailSharedMailboxScreen',
  'MailAccountShareScreen',
  'MailScheduledScreen',
  // VoIP screens render their own premium management top bar.
  'VoipSettingsScreen',
  'DialerScreen',
  'CallHistoryScreen',
  'DirectoryScreen',
]);

const SCREEN_TITLE_KEYS = {
  StartScreen: 'start_screen_title',
  LoginScreen: 'login_screen_title',
  HomeScreen: 'home_screen_title',
  TablesScreen: 'tables_screen_title',
  PosHubScreen: 'pos_hub_title',
  OnlineScreen: 'online_screen_title',
  TermsOfServicesScreen: 'terms_of_services_screen_title',
  DayOpeningScreen: 'day_opening_screen_title',
  DayOpeningConfirmationScreen: 'day_opening_confirmation_screen_title',
  DayClosingScreen: 'day_closing_screen_title',
  DayClosingScreenExtended: 'day_closing_screen_title',
  StatisticsScreen: 'statistics_screen_title',
  OrderListScreen: 'order_list_screen_title',
  SuccessDayClosingScreen: 'success_day_closing_screen_title',
  PrivacyPolicyScreen: 'privacy_policy_screen_title',
  PaymentsSettingsScreen: 'payments_settings_screen_title',
  CashierWifiSettingsScreen: 'cashier_wifi_settings_screen_title',
  SettingsScreen: 'settings_screen_title',
  DesignSettingsScreen: 'design_settings_screen_title',
  SettingsPrinterSearchScreen: 'settings_printer_search_screen_title',
  SettingsPrinterPairingScreen: 'settings_printer_pairing_screen_title',
  PrinterProfilesScreen: 'printer_profiles_screen_title',
  PrinterProfileEditScreen: 'printer_profile_edit_screen_title',
  SupportChatScreen: 'support_chat_screen_title',
  GuestSupportChatScreen: 'guest_chat_screen_title',
  OrderDetailsScreen: 'order_details_screen_title',
  ScanDevicesScreen: 'scan_devices_screen_title',
  NotificationDetailScreen: 'notif_detail_title',
  NotificationsScreen: 'notifications_screen_title',
  VipUserScreen: 'vip_user_screen_title',
  AddVipUserScreen: 'add_vip_user_screen_title',
  VipUserDetailScreen: 'vip_user_detail_screen_title',
  PaymentScreen: 'payment_screen_title',
  SuccessPayment: 'success_payment_screen_title',
  SuccessPaymentScreen: 'success_payment_screen_title',
  FailedPayment: 'failed_payment_screen_title',
  FailedPaymentScreen: 'failed_payment_screen_title',
  TicketEntry: 'ticket_entry_screen_title',
  TicketEntryScreen: 'ticket_entry_screen_title',
  SuccessEntryScreen: 'success_entry_screen_title',
  FailedEntryScreen: 'failed_entry_screen_title',
  ResultScreen: 'result_screen_title',
  PendingOrdersScreen: 'pending_orders_screen_title',
  TablesBookingsScreen: 'tables_bookings_screen_title',
  WaitingForPayment: 'waiting_for_payment_screen_title',
  OfflineSyncStatusScreen: 'offline_sync_status_screen_title',
  TimeTrackerScreen: 'tt_screen_title',
  HaccpScreen: 'haccp_screen_title',
  MeetingRecorderScreen: 'meeting_recorder_title',
  MeetingDetailScreen: 'meeting_detail_title',
  ChatHomeScreen: 'team_chat_hub_title',
  ChatChannelScreen: 'team_chat_title',
  ChatThreadScreen: 'team_chat_thread_title',
  ChatChannelInfoScreen: 'team_chat_channel_info',
  ChatNewScreen: 'team_chat_new',
  ChatSettingsScreen: 'team_chat_my_preferences',
  ChatModuleAppsScreen: 'team_chat_mapp_title',
  ChatMeetingScreen: 'team_chat_meeting_default_title',
  ManagementHomeScreen: 'management_screen_title',
  ManagementDashboardCustomizeScreen: 'mgmt_dash_customize_title',
  ManagementListScreen: 'management_list_screen_title',
  ManagementAutomationsScreen: 'mgmt_automations_topbar_title',
  ManagementDetailScreen: 'management_detail_screen_title',
  // Sales module. Dotted `sales.*` keys, like every other Sales string — the
  // registrations below ALSO pass an explicit Hungarian `title` override so a
  // build whose locale bundle has not caught up yet can never surface the raw
  // key (the value still reaches the web document title with the header hidden).
  SalesHomeScreen: 'sales.nav.home',
  SalesDashboardScreen: 'sales.nav.dashboard',
  SalesOrdersScreen: 'sales.nav.orders',
  SalesOrderBuilderScreen: 'sales.nav.newOrder',
  SalesOrderReviewScreen: 'sales.nav.orderReview',
  SalesCustomerDetailScreen: 'sales.nav.customerDetail',
  SalesFollowUpsScreen: 'sales.fu.title',
  // Apollo.io prospecting. Dotted `apollo.*` keys, and — like the Sales rows
  // above — the registrations pass an explicit Hungarian `title` override, so a
  // build whose locale bundle has not caught up yet never surfaces the raw key.
  ApolloSearchScreen: 'apollo.screen.title',
  ApolloSettingsScreen: 'apollo.settings.topBarTitle',
};

const AppNavigator = ({ isLoggedIn, isServiceMode, isStockMode, isManagerMode, isSalesMode, isDriverMode, isDayOpen, currentRestaurant, openAdmin }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  // Keep the floating subscription banner clear of the notch / Dynamic Island.
  const topInset = useTopInset();
  const {maybeStartAppTutorial} = useContext(TourContext);
  const autoTutorialRequested = useRef(false);
  // `pos` is the POS till module (renamed from `sales`, which now names the new
  // CRM field-sales module — see config/appVariantManifest.js).
  const posEnabled = isAppModuleEnabled('pos');
  const managementEnabled = isAppModuleEnabled('management');
  const serviceEnabled = isAppModuleEnabled('service');
  const warehouseEnabled = isAppModuleEnabled('warehouse');
  // Driver / courier (Sofor) app — van stock, route execution, field sales.
  const driverEnabled = isAppModuleEnabled('driver');
  // Sales = the CRM field-sales module (app mode `sales`), NOT the POS till.
  const salesEnabled = isAppModuleEnabled('sales');
  // The Sales module reuses the shared, config-driven management list/detail
  // screens for its Leads and Customers tabs, so those three routes must be
  // registered whenever EITHER bundle ships. Deliberately not done by turning
  // `modules.management` on for the sales variant: that would also mount
  // ManagementDataProvider (background order polling) and the whole Management
  // prefetch chain in an app that has no Management home at all.
  const sharedManagementScreens = managementEnabled || salesEnabled;
  const initialRouteName = useMemo(() => {
    if (!isLoggedIn) {
      return 'StartScreen';
    }

    // Only target a module home when that module is actually bundled in this
    // build — otherwise the screen isn't registered and React Navigation throws
    // "Property '<Screen>' doesn't exist" (e.g. a service build resolving stock).
    if (isSalesMode && salesEnabled) {
      return 'SalesHomeScreen';
    }

    if (isDriverMode && driverEnabled) {
      return 'DriverHomeScreen';
    }

    if (isManagerMode && managementEnabled) {
      return 'ManagementHomeScreen';
    }

    if (isServiceMode && serviceEnabled) {
      return 'RepairmanHomeScreen';
    }

    if (isStockMode && warehouseEnabled) {
      return 'WarehousemanHomeScreen';
    }

    if (posEnabled) {
      return isDayOpen === true ? getPosEntryRoute() : 'DayOpeningScreen';
    }

    // The active mode's module isn't part of this build; fall back to whichever
    // module this variant ships so we never land on an unregistered screen.
    if (serviceEnabled) return 'RepairmanHomeScreen';
    if (warehouseEnabled) return 'WarehousemanHomeScreen';
    if (managementEnabled) return 'ManagementHomeScreen';
    if (salesEnabled) return 'SalesHomeScreen';
    if (driverEnabled) return 'DriverHomeScreen';
    return 'StartScreen';
  }, [driverEnabled, isDriverMode, isDayOpen, isLoggedIn, isManagerMode, isSalesMode, isServiceMode, isStockMode, posEnabled, salesEnabled, serviceEnabled, warehouseEnabled, managementEnabled]);

  useEffect(() => {
    if (!isLoggedIn || autoTutorialRequested.current) {
      return undefined;
    }

    // A notification-driven launch is already heading for one specific screen.
    // Popping the walkthrough over it ~900 ms later would cover exactly what the
    // user tapped, so stand down for this run — `autoTutorialRequested` is left
    // untouched, so the tutorial is simply offered on the next ordinary launch.
    if (hasNotificationLaunchIntent()) {
      return undefined;
    }

    // Sales (CRM field sales) has no walkthrough of its own, and the POS tour
    // would be the wrong one entirely — stand down rather than mis-tour a rep.
    if (isSalesMode || isDriverMode || (!isManagerMode && !isServiceMode && !isStockMode && isDayOpen !== true)) {
      return undefined;
    }

    autoTutorialRequested.current = true;
    const timer = globalThis.setTimeout(() => {
      maybeStartAppTutorial({
        isManagerMode,
        isServiceMode,
        isStockMode,
        source: 'auto',
        delay: 350,
      });
    }, 900);

    return () => globalThis.clearTimeout(timer);
  }, [isDayOpen, isLoggedIn, isManagerMode, isServiceMode, isStockMode, isSalesMode, isDriverMode, maybeStartAppTutorial]);

  useEffect(() => {
    if (!isLoggedIn) {
      return undefined;
    }

    const prefetchTimer = globalThis.setTimeout(() => {
      // Fire-and-forget screen prefetch — warms the module-eval of the screens the
      // user is most likely to open next IN THE ACTIVE MODE, so the first
      // navigation there doesn't pay a cold inlineRequires eval + the per-screen
      // Suspense spinner. Previously only sales screens were warmed, leaving the
      // service / warehouse / management first-nav cold. Do NOT use
      // Promise.allSettled here: on the Hermes build this app ships, the native
      // Promise lacks it (and the global polyfill does not reliably patch the
      // intrinsic), which crashed the app with a release-only "undefined is not a
      // function" the moment this effect ran on an already-logged-in relaunch.
      // Settling each promise individually needs only Promise.prototype.then,
      // which Hermes always provides.
      const loaders = [loadSettingsScreen, loadNotificationsScreen]; // universal

      if (isManagerMode) {
        loaders.push(
          () => import('../screens/ManagementListScreen'),
          () => import('../screens/ManagementDetailScreen'),
        );
      } else if (isServiceMode) {
        loaders.push(
          () => import('../screens/WorkSheetListScreen'),
          () => import('../screens/WorkSheetDetailScreen'),
        );
      } else if (isStockMode) {
        loaders.push(
          () => import('../screens/WarehouseReceivingListScreen'),
          () => import('../screens/WarehousePickingListScreen'),
        );
      } else if (isDriverMode) {
        // A courier lands on the route tab; the stop screen is the next tap.
        loaders.push(
          () => import('../screens/driver/DriverRouteDetailScreen'),
          () => import('../screens/driver/DriverStopScreen'),
        );
      } else if (isSalesMode) {
        // A rep's first two taps are almost always a list (Leads / Customers)
        // and the scanner-first sale, so warm exactly those.
        loaders.push(
          () => import('../screens/ManagementListScreen'),
          () => import('../screens/sales/SalesOrderBuilderScreen'),
        );
      } else if (posEnabled) {
        loaders.push(
          loadOnlineScreen,
          loadOrderDetailsScreen,
          loadTablesScreen,
          loadOrdersScreen,
        );
      }

      loaders.forEach((load) => {
        try {
          const pending = load();
          if (pending && typeof pending.then === 'function') {
            // Swallow rejections — screens still load lazily on navigation.
            pending.then(undefined, () => {});
          }
        } catch (_) {
          // Ignore synchronous prefetch failures.
        }
      });
    }, 1200);

    return () => globalThis.clearTimeout(prefetchTimer);
  }, [isLoggedIn, posEnabled, isManagerMode, isServiceMode, isStockMode, isSalesMode, isDriverMode]);

  // NOTE: isDayOpen is intentionally NOT part of navigatorKey. It used to be,
  // which force-REMOUNTED the entire navigator (every screen + ManagementDataProvider)
  // on day open/close — an expensive, janky teardown/rebuild. Day open/close
  // already navigates imperatively (DayOpeningScreen → HomeScreen), so the user
  // lands correctly without nuking the whole stack. Only a genuine identity
  // change (login state / app mode) warrants a full remount.
  const navigatorKey = useMemo(() => {
    return [
      isLoggedIn ? 'logged-in' : 'logged-out',
      isManagerMode ? 'manager' : 'no-manager',
      isServiceMode ? 'service' : 'no-service',
      isStockMode ? 'stock' : 'no-stock',
      isSalesMode ? 'sales' : 'no-sales',
      isDriverMode ? 'driver' : 'no-driver',
    ].join(':');
  }, [isLoggedIn, isManagerMode, isServiceMode, isStockMode, isSalesMode, isDriverMode]);

  // Memoize the transition spec to prevent recreation on each render
  const transitionSpec = useMemo(() => ({
    open: {
      animation: 'timing',
      config: { duration: 150 }
    },
    close: {
      animation: 'timing',
      config: { duration: 150 }
    },
  }), []);

  // Platform-specific navigation feel. On iOS we adopt the native push/pop
  // (SlideFromRightIOS) with an edge-to-edge interactive back-swipe and a
  // centered header title — the standard UINavigationController behaviour. On
  // Android we keep the fast custom 150 ms timing + left-aligned Material title.
  const platformNavOptions = useMemo(() => {
    if (Platform.OS === 'ios') {
      return {
        ...TransitionPresets.SlideFromRightIOS,
        gestureResponseDistance: Dimensions.get('screen').width,
        headerTitleAlign: 'center',
      };
    }
    return {
      transitionSpec,
      headerTitleAlign: 'left',
    };
  }, [transitionSpec]);

  const getScreenOptions = useMemo(() => {
    return (name, overrides = {}) => {
      const titleKey = SCREEN_TITLE_KEYS[name];

      return {
        headerShown: !HIDDEN_HEADER_ROUTES.has(name),
        title: titleKey ? t(titleKey) : name,
        ...overrides,
      };
    };
  }, [t]);

  return (
    <>
      {/* NetworkMonitor removed: its sole effect (processQueue on connectivity
          return) is already covered by SyncManager.startNetworkListener, which
          is always active here via OfflineSyncProvider and uses a superset
          connectivity predicate. Keeping both just registered a duplicate
          NetInfo listener. */}
      <ConnectivityIndicator />
      {/* Daily "Mai napom" briefing — mounted once here (inside NavigationContainer + SystemProvider, above every
          module's screens) so it opens on the first launch of each day in ANY module / app mode. Supersedes the
          old Sales/Management-only FollowUpAutoOpen (which opened the Teendők screen instead). */}
      <MyDayBriefing enabled={!!isLoggedIn} />
      <SyncBadge />
      <PrintQueueIndicator />

      {/* Subscription warning banner */}
      {currentRestaurant && currentRestaurant?.customerStatus === "pausedForPayment" && (
        <View style={{
          position: "absolute",
          top: topInset + 8,
          zIndex: 999,
          width: Dimensions.get("screen").width - 60,
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: '#f8d7da',
          padding: 10,
          borderRadius: 10,
          marginLeft: 30,
          marginRight: 30,
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.25,
          shadowRadius: 3.84,
          elevation: 5,
        }}>
          <Ionicons name="alert-circle-outline" size={24}
            color="#721c24" style={{ marginRight: 10 }} />
          <Text style={{
            color: '#721c24',
            textAlign: 'center',
            flex: 1,
            fontWeight: '700'
          }}>
            {t("failedSubscriptionFee_alertTextHead")}{"\n"}{t("failedSubscriptionFee_alertTextSub")}
          </Text>
          <TouchableOpacity
            style={{
              backgroundColor: 'orange',
              paddingVertical: 8,
              paddingHorizontal: 16,
              borderRadius: 5,
              marginLeft: 'auto',
            }}
            onPress={openAdmin}
          >
            <Text style={{
              color: '#fff',
              fontWeight: 'bold'
            }}>{t("failedSubscriptionFee_solvePayment")}</Text>
          </TouchableOpacity>
        </View>
      )}

      <MaybeManagementData enabled={managementEnabled}>
      <Stack.Navigator
        key={navigatorKey}
        initialRouteName={initialRouteName}
        screenOptions={{
          ...platformNavOptions,
          gestureEnabled: true,
          headerShown: true,
          headerBackTitleVisible: false,
          cardStyle: {
            flex: 1,
            minHeight: 0,
          },
          headerTintColor: theme.colors.text,
          // Munkanap (workday) pill in EVERY native stack header. No screen sets its own `headerRight` today; a screen
          // that does must compose this pill in front of its content rather than replace it.
          headerRight: () => <WorkdayHeaderPill compact style={{ marginRight: 12 }} />,
          headerStyle: {
            backgroundColor: theme.colors.card,
          },
          headerTitleStyle: {
            fontWeight: '700',
            color: theme.colors.text,
          },
        }}>
        <Stack.Screen
          name="StartScreen"
          component={StartScreen}
          options={getScreenOptions('StartScreen', {
            gestureEnabled: true,
            transitionSpec: {
              open: {
                animation: 'timing',
                config: { duration: 500 }
              },
              close: {
                animation: 'timing',
                config: { duration: 500 }
              },
            },
          })}
        />

        <Stack.Screen name="LoginScreen" component={LoginScreen} options={getScreenOptions('LoginScreen')} />
        <Stack.Screen
          name="ForgotPasswordScreen"
          component={ForgotPasswordScreen}
          options={getScreenOptions('ForgotPasswordScreen', {
            headerShown: true,
            title: t('forgot_password_screen_title', 'Elfelejtett jelszó'),
          })}
        />
        <Stack.Screen name="NotificationDetailScreen" component={NotificationDetailScreen} options={getScreenOptions('NotificationDetailScreen')} />
        <Stack.Screen name="AdminWebScreen" component={AdminWebScreen} options={getScreenOptions('AdminWebScreen')} />
        <Stack.Screen name="NotificationsScreen" component={NotificationsScreen} options={getScreenOptions('NotificationsScreen')} />
        <Stack.Screen name="IncomingCall" component={IncomingCallScreen} options={getScreenOptions('IncomingCall', { headerShown: false, gestureEnabled: false })} />

        <Stack.Screen name="SettingsPrinterPairingScreen" component={SettingsPrinterPairingScreen} options={getScreenOptions('SettingsPrinterPairingScreen')} />
        <Stack.Screen name="PrinterProfilesScreen" component={PrinterProfilesScreen} options={getScreenOptions('PrinterProfilesScreen')} />
        <Stack.Screen name="PrinterProfileEditScreen" component={PrinterProfileEditScreen} options={getScreenOptions('PrinterProfileEditScreen')} />
        <Stack.Screen name="TermsOfServicesScreen" component={TermsOfServicesScreen} options={getScreenOptions('TermsOfServicesScreen')} />
        <Stack.Screen name="ScanDevicesScreen" component={ScanDevicesScreen} options={getScreenOptions('ScanDevicesScreen')} />
        <Stack.Screen name="PrivacyPolicyScreen" component={PrivacyPolicyScreen} options={getScreenOptions('PrivacyPolicyScreen')} />
        <Stack.Screen name="DesignSettingsScreen" component={DesignSettingsScreen} options={getScreenOptions('DesignSettingsScreen')} />
        <Stack.Screen name="SupportChatScreen" component={SupportChatScreen} options={getScreenOptions('SupportChatScreen')} />
        <Stack.Screen name="GuestSupportChatScreen" component={GuestSupportChatScreen} options={getScreenOptions('GuestSupportChatScreen')} />
        <Stack.Screen name="SettingsPrinterSearchScreen" component={SettingsPrinterSearchScreen} options={getScreenOptions('SettingsPrinterSearchScreen')} />
        <Stack.Screen name="SettingsScreen" component={SettingsScreen} options={getScreenOptions('SettingsScreen')} />
        <Stack.Screen name="ProfileEditScreen" component={ProfileEditScreen} options={getScreenOptions('ProfileEditScreen')} />
        <Stack.Screen name="ChangePasswordScreen" component={ChangePasswordScreen} options={getScreenOptions('ChangePasswordScreen')} />
        <Stack.Screen name="TwoFactorScreen" component={TwoFactorScreen} options={getScreenOptions('TwoFactorScreen')} />
        <Stack.Screen name="NotificationSettingsScreen" component={NotificationSettingsScreen} options={getScreenOptions('NotificationSettingsScreen')} />
        <Stack.Screen name="MeetingRecordingSettingsScreen" component={MeetingRecordingSettingsScreen} options={getScreenOptions('MeetingRecordingSettingsScreen')} />
        <Stack.Screen name="MeetingRecorderScreen" component={MeetingRecorderScreen} options={getScreenOptions('MeetingRecorderScreen')} />
        <Stack.Screen name="MeetingDetailScreen" component={MeetingDetailScreen} options={getScreenOptions('MeetingDetailScreen')} />
        <Stack.Screen name="AiAgentSettingsScreen" component={AiAgentSettingsScreen} options={getScreenOptions('AiAgentSettingsScreen')} />
        {/* "Ask anything" AI assistant — universal across every app mode, like the mail client below. */}
        <Stack.Screen name="AIAssistantScreen" component={AIAssistantScreen} options={getScreenOptions('AIAssistantScreen')} />
        {/* Team Chat + meetings — universal across every app mode (see the lazy imports above). */}
        <Stack.Screen name="ChatHomeScreen" component={ChatHomeScreen} options={getScreenOptions('ChatHomeScreen')} />
        <Stack.Screen name="ChatChannelScreen" component={ChatChannelScreen} options={getScreenOptions('ChatChannelScreen')} />
        <Stack.Screen name="ChatThreadScreen" component={ChatThreadScreen} options={getScreenOptions('ChatThreadScreen')} />
        <Stack.Screen name="ChatChannelInfoScreen" component={ChatChannelInfoScreen} options={getScreenOptions('ChatChannelInfoScreen')} />
        <Stack.Screen name="ChatNewScreen" component={ChatNewScreen} options={getScreenOptions('ChatNewScreen')} />
        <Stack.Screen name="ChatSettingsScreen" component={ChatSettingsScreen} options={getScreenOptions('ChatSettingsScreen')} />
        <Stack.Screen name="ChatModuleAppsScreen" component={ChatModuleAppsScreen} options={getScreenOptions('ChatModuleAppsScreen')} />
        <Stack.Screen name="ChatMeetingScreen" component={ChatMeetingScreen} options={getScreenOptions('ChatMeetingScreen', { gestureEnabled: false })} />
        <Stack.Screen name="WidgetsScreen" component={WidgetsScreen} options={getScreenOptions('WidgetsScreen')} />
        <Stack.Screen name="InsightWidgetSettingsScreen" component={InsightWidgetSettingsScreen} options={getScreenOptions('InsightWidgetSettingsScreen')} />
        <Stack.Screen name="LoggedInDevicesScreen" component={LoggedInDevicesScreen} options={getScreenOptions('LoggedInDevicesScreen')} />
        <Stack.Screen name="OtherSettingsScreen" component={OtherSettingsScreen} options={getScreenOptions('OtherSettingsScreen', { title: t('settings_screen_title') })} />
        {/* Time tracker — universal, like the mail client: a Service technician,
            a Management user and a home-screen widget deep link all reach it. */}
        <Stack.Screen name="TimeTrackerScreen" component={TimeTrackerScreen} options={getScreenOptions('TimeTrackerScreen')} />
        <Stack.Screen name="ShareToHappyScreen" component={ShareToHappyScreen} options={getScreenOptions('ShareToHappyScreen')} />
        <Stack.Screen name="OfflineSyncStatusScreen" component={OfflineSyncStatusScreen} options={getScreenOptions('OfflineSyncStatusScreen')} />

        {/* Mail client — universal across every app mode (Sales / Service / Logistics / Management). */}
        <Stack.Screen name="MailHomeScreen" component={MailHomeScreen} options={getScreenOptions('MailHomeScreen')} />
        <Stack.Screen name="MailThreadScreen" component={MailThreadScreen} options={getScreenOptions('MailThreadScreen')} />
        <Stack.Screen name="MailComposeScreen" component={MailComposeScreen} options={getScreenOptions('MailComposeScreen')} />
        <Stack.Screen name="MailSearchScreen" component={MailSearchScreen} options={getScreenOptions('MailSearchScreen')} />
        <Stack.Screen name="MailAccountWizardScreen" component={MailAccountWizardScreen} options={getScreenOptions('MailAccountWizardScreen')} />
        <Stack.Screen name="MailSettingsScreen" component={MailSettingsScreen} options={getScreenOptions('MailSettingsScreen')} />
        <Stack.Screen name="MailRulesScreen" component={MailRulesScreen} options={getScreenOptions('MailRulesScreen')} />
        <Stack.Screen name="MailRuleEditorScreen" component={MailRuleEditorScreen} options={getScreenOptions('MailRuleEditorScreen')} />
        <Stack.Screen name="MailDesignTemplatesScreen" component={MailDesignTemplatesScreen} options={getScreenOptions('MailDesignTemplatesScreen')} />
        <Stack.Screen name="MailTemplatesScreen" component={MailTemplatesScreen} options={getScreenOptions('MailTemplatesScreen')} />
        <Stack.Screen name="MailSecurityScreen" component={MailSecurityScreen} options={getScreenOptions('MailSecurityScreen')} />
        <Stack.Screen name="MailSharedMailboxScreen" component={MailSharedMailboxScreen} options={getScreenOptions('MailSharedMailboxScreen')} />
        <Stack.Screen name="MailAccountShareScreen" component={MailAccountShareScreen} options={getScreenOptions('MailAccountShareScreen')} />
        <Stack.Screen name="MailScheduledScreen" component={MailScheduledScreen} options={getScreenOptions('MailScheduledScreen')} />

        {/* VoIP softphone — universal across every app mode. */}
        <Stack.Screen name="DialerScreen" component={DialerScreen} options={getScreenOptions('DialerScreen', { headerShown: false })} />
        <Stack.Screen name="CallHistoryScreen" component={CallHistoryScreen} options={getScreenOptions('CallHistoryScreen', { headerShown: false })} />
        <Stack.Screen name="DirectoryScreen" component={DirectoryScreen} options={getScreenOptions('DirectoryScreen', { headerShown: false })} />

        {/* Procurement (Beszerzés) — universal across every app mode: the POS
            "Funkciók" screen reaches igénylések/megrendelések directly, the
            Management hub and its bottom bar reach the whole module. Every
            screen draws its own header, so headerShown stays off. */}
        <Stack.Screen name="ProcurementHomeScreen" component={ProcurementHomeScreen} options={{ headerShown: false }} />
        <Stack.Screen name="ProcurementRequisitionsScreen" component={ProcurementRequisitionsScreen} options={{ headerShown: false }} />
        <Stack.Screen name="ProcurementRequisitionDetailScreen" component={ProcurementRequisitionDetailScreen} options={{ headerShown: false }} />
        <Stack.Screen name="ProcurementRfqsScreen" component={ProcurementRfqsScreen} options={{ headerShown: false }} />
        <Stack.Screen name="ProcurementRfqDetailScreen" component={ProcurementRfqDetailScreen} options={{ headerShown: false }} />
        <Stack.Screen name="ProcurementOrdersScreen" component={ProcurementOrdersScreen} options={{ headerShown: false }} />
        <Stack.Screen name="ProcurementOrderDetailScreen" component={ProcurementOrderDetailScreen} options={{ headerShown: false }} />
        <Stack.Screen name="ProcurementSourcingScreen" component={ProcurementSourcingScreen} options={{ headerShown: false }} />
        <Stack.Screen name="ProcurementReorderRulesScreen" component={ProcurementReorderRulesScreen} options={{ headerShown: false }} />
        <Stack.Screen name="ProcurementSettingsScreen" component={ProcurementSettingsScreen} options={{ headerShown: false }} />

        {/* Utility bills & meters (Rezsi nyilvántartás) — universal like
            Procurement; every screen draws its own header. */}
        <Stack.Screen name="UtilityHomeScreen" component={UtilityHomeScreen} options={{ headerShown: false }} />
        <Stack.Screen name="UtilityProvidersScreen" component={UtilityProvidersScreen} options={{ headerShown: false }} />
        <Stack.Screen name="UtilityProviderDetailScreen" component={UtilityProviderDetailScreen} options={{ headerShown: false }} />
        <Stack.Screen name="UtilityReadingScreen" component={UtilityReadingScreen} options={{ headerShown: false }} />
        <Stack.Screen name="UtilityBillScreen" component={UtilityBillScreen} options={{ headerShown: false }} />
        <Stack.Screen name="UtilityAnomaliesScreen" component={UtilityAnomaliesScreen} options={{ headerShown: false }} />

        {/* Fleet (Flotta) — every screen draws its own header. */}
        <Stack.Screen name="ManagementFleetStatsScreen" component={ManagementFleetStatsScreen} options={{ headerShown: false }} />
        <Stack.Screen name="ManagementRefuelsScreen" component={ManagementRefuelsScreen} options={{ headerShown: false }} />
        <Stack.Screen name="ManagementDriverBreaksScreen" component={ManagementDriverBreaksScreen} options={{ headerShown: false }} />
        <Stack.Screen name="VoipSettingsScreen" component={VoipSettingsScreen} options={getScreenOptions('VoipSettingsScreen', { headerShown: false })} />

        {posEnabled && (
          <>
            <Stack.Screen name="HomeScreen" component={PosHomeScreen} options={getScreenOptions('HomeScreen')} />
            <Stack.Screen name="PosHubScreen" component={PosHubScreenThemed} options={getScreenOptions('PosHubScreen')} />
            <Stack.Screen name="OnlineScreen" component={PosOnlineScreen} options={getScreenOptions('OnlineScreen')} />
            <Stack.Screen name="OrderDetailsScreen" component={OrderDetailsScreen} options={getScreenOptions('OrderDetailsScreen')} />
            <Stack.Screen name="SuccessEntryScreen" component={SuccessEntryScreen} options={getScreenOptions('SuccessEntryScreen')} />
            <Stack.Screen name="SuccessPayment" component={SuccessPaymentScreen} options={getScreenOptions('SuccessPayment')} />
            <Stack.Screen name="SuccessPaymentScreen" component={SuccessPaymentScreen} options={getScreenOptions('SuccessPaymentScreen')} />
            <Stack.Screen name="TablesScreen" component={PosTablesScreen} options={getScreenOptions('TablesScreen')} />
            <Stack.Screen name="VipUserScreen" component={VipUserScreen} options={getScreenOptions('VipUserScreen')} />
            <Stack.Screen name="VipUserDetailScreen" component={VipUserDetailScreen} options={getScreenOptions('VipUserDetailScreen')} />
            <Stack.Screen name="PaymentScreen" component={PosPaymentScreen} options={getScreenOptions('PaymentScreen')} />
            <Stack.Screen name="ResultScreen" component={ResultScreen} options={getScreenOptions('ResultScreen')} />
            <Stack.Screen name="WaitingForPayment" component={WaitingForPayment} options={getScreenOptions('WaitingForPayment')} />
            <Stack.Screen name="FailedPayment" component={FailedPaymentScreen} options={getScreenOptions('FailedPayment')} />
            <Stack.Screen name="FailedPaymentScreen" component={FailedPaymentScreen} options={getScreenOptions('FailedPaymentScreen')} />
            <Stack.Screen name="TicketEntry" component={TicketEntryScreen} options={getScreenOptions('TicketEntry')} />
            <Stack.Screen name="TicketEntryScreen" component={TicketEntryScreen} options={getScreenOptions('TicketEntryScreen')} />
            <Stack.Screen name="FailedEntryScreen" component={FailedEntryScreen} options={getScreenOptions('FailedEntryScreen')} />
            <Stack.Screen name="PaymentsSettingsScreen" component={PaymentsSettingsScreen} options={getScreenOptions('PaymentsSettingsScreen')} />
        <Stack.Screen name="CashierWifiSettingsScreen" component={CashierWifiSettingsScreen} options={getScreenOptions('CashierWifiSettingsScreen')} />
            <Stack.Screen name="DayClosingScreen" component={DayClosingScreenExtended} options={getScreenOptions('DayClosingScreen')} />
            <Stack.Screen name="DayClosingScreenExtended" component={DayClosingScreenExtended} options={getScreenOptions('DayClosingScreenExtended')} />
            <Stack.Screen name="DayClosingScreenBasic" component={DayClosingScreenBasic} options={getScreenOptions('DayClosingScreenBasic', { title: t('day_closing_screen_title') })} />
            <Stack.Screen name="DayOpeningScreen" component={DayOpeningScreen} options={getScreenOptions('DayOpeningScreen')} />
            <Stack.Screen name="DayOpeningConfirmationScreen" component={DayOpeningConfirmationScreen} options={getScreenOptions('DayOpeningConfirmationScreen')} />
            <Stack.Screen name="SuccessDayClosingScreen" component={SuccessDayClosingScreen} options={getScreenOptions('SuccessDayClosingScreen')} />
            <Stack.Screen name="StatisticsScreen" component={StatisticsScreen} options={getScreenOptions('StatisticsScreen')} />
            <Stack.Screen name="OrderListScreen" component={OrderListScreen} options={getScreenOptions('OrderListScreen')} />
            <Stack.Screen name="AddVipUserScreen" component={AddVipUserScreen} options={getScreenOptions('AddVipUserScreen')} />
            <Stack.Screen name="OrdersScreen" component={OrdersScreen} options={getScreenOptions('OrdersScreen', { title: t('order_list_screen_title') })} />
            <Stack.Screen name="PendingOrdersScreen" component={OrdersScreen} options={getScreenOptions('PendingOrdersScreen')} />
            <Stack.Screen name="TablesBookingsScreen" component={TablesBookingsScreen} options={getScreenOptions('TablesBookingsScreen')} />
            <Stack.Screen name="ProductTrafficScreen" component={ProductTrafficScreen} options={getScreenOptions('ProductTrafficScreen', { title: t('statistics_screen_title') })} />
            <Stack.Screen name="NovoTerminalSettingsScreen" component={NovoTerminalSettingsScreen} options={getScreenOptions('NovoTerminalSettingsScreen', { title: t('payments_settings_screen_title') })} />
            <Stack.Screen name="Sam4sSettingsScreen" component={Sam4sSettingsScreen} options={getScreenOptions('Sam4sSettingsScreen', { title: t('payments_settings_screen_title') })} />
            <Stack.Screen name="ExpensesScreen" component={ExpensesScreen} options={getScreenOptions('ExpensesScreen', { title: t('expenses') })} />
            <Stack.Screen name="BookingDetailsScreen" component={BookingDetailsScreen} options={getScreenOptions('BookingDetailsScreen', { title: t('order_details_screen_title') })} />
            <Stack.Screen name="HaccpScreen" component={HaccpScreen} options={getScreenOptions('HaccpScreen')} />
            <Stack.Screen name="FunctionsScreen" component={FunctionsScreen} options={getScreenOptions('FunctionsScreen', { title: t('functions') || 'Funkciók' })} />
          </>
        )}

        {/* Shared, config-driven management screens. Registered for the Management
            bundle AND for the Sales module, which reuses them verbatim for its
            Leads / Customers tabs and every record it opens — without this the
            standalone `sales` variant (modules.management: false) would throw
            "The action 'NAVIGATE' with payload … was not handled" on the very
            first row tap. */}
        {sharedManagementScreens && (
          <>
            <Stack.Screen name="ManagementListScreen" component={ManagementListScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ManagementDetailScreen" component={ManagementDetailScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ManagementCrmScreen" component={ManagementCrmScreen} options={{ headerShown: false }} />
            {/* Apollo.io prospecting — same shared placement as the CRM hub above:
                reached from ManagementCrmScreen in Management mode and from the
                Sales "Egyéb" hub in Sales mode, so it must exist in BOTH bundles. */}
            <Stack.Screen name="ApolloSearchScreen" component={ApolloSearchScreen} options={getScreenOptions('ApolloSearchScreen', { title: t('apollo.screen.title', 'Apollo keresés') })} />
            <Stack.Screen name="ApolloSettingsScreen" component={ApolloSettingsScreen} options={getScreenOptions('ApolloSettingsScreen', { title: t('apollo.settings.topBarTitle', 'Apollo.io') })} />
            <Stack.Screen name="SalesFollowUpsScreen" component={SalesFollowUpsScreen} options={getScreenOptions('SalesFollowUpsScreen', { title: t('sales.fu.title', 'Teendők') })} />
          </>
        )}

        {/* Sales (CRM field-sales) module screens */}
        {salesEnabled && (
          <>
            <Stack.Screen name="SalesHomeScreen" component={SalesHomeScreen} options={getScreenOptions('SalesHomeScreen', { title: t('sales.nav.home', 'Értékesítés') })} />
            <Stack.Screen name="SalesDashboardScreen" component={SalesDashboardScreen} options={getScreenOptions('SalesDashboardScreen', { title: t('sales.nav.dashboard', 'Áttekintés') })} />
            <Stack.Screen name="SalesOrdersScreen" component={SalesOrdersScreen} options={getScreenOptions('SalesOrdersScreen', { title: t('sales.nav.orders', 'Rendelések') })} />
            <Stack.Screen name="SalesOrderBuilderScreen" component={SalesOrderBuilderScreen} options={getScreenOptions('SalesOrderBuilderScreen', { title: t('sales.nav.newOrder', 'Új eladás') })} />
            <Stack.Screen name="SalesOrderReviewScreen" component={SalesOrderReviewScreen} options={getScreenOptions('SalesOrderReviewScreen', { title: t('sales.nav.orderReview', 'Rendelés összegzése') })} />
            <Stack.Screen name="SalesCustomerDetailScreen" component={SalesCustomerDetailScreen} options={getScreenOptions('SalesCustomerDetailScreen', { title: t('sales.nav.customerDetail', 'Ügyfél') })} />
            {/* Order rows in the Sales module open the SHARED order-details screen,
                which otherwise ships only with the POS bundle — and the `sales`
                variant has modules.pos: false. The `!posEnabled` guard keeps the
                route registered exactly once in the global build. */}
            {!posEnabled && (
              <Stack.Screen name="OrderDetailsScreen" component={OrderDetailsScreen} options={getScreenOptions('OrderDetailsScreen')} />
            )}
          </>
        )}

        {/* Management module screens */}
        {managementEnabled && (
          <>
            <Stack.Screen name="ManagementHomeScreen" component={ManagementHomeScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ManagementStoreSettingsScreen" component={ManagementStoreSettingsScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ManagementBillingScreen" component={ManagementBillingScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ManagementUsersScreen" component={ManagementUsersScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ManagementRolesScreen" component={ManagementRolesScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ManagementModuleTemplatesScreen" component={ManagementModuleTemplatesScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ManagementServiceModesScreen" component={ManagementServiceModesScreen} options={{ headerShown: false }} />
            {/* ManagementCrmScreen moved up into the shared block — the Sales
                module reaches it through the `salesModule` bottom bar. */}
            <Stack.Screen name="ManagementQuoteEditorScreen" component={ManagementQuoteEditorScreen} options={{ headerShown: false }} />
            <Stack.Screen name="QuoteTemplatePickerScreen" component={QuoteTemplatePickerScreen} options={{ headerShown: false }} />
            <Stack.Screen name="QuoteDocumentScreen" component={QuoteDocumentScreen} options={{ headerShown: false }} />
            <Stack.Screen name="DocumentGalleryPickerScreen" component={DocumentGalleryPickerScreen} options={{ headerShown: false }} />
            <Stack.Screen name="DocumentDocumentScreen" component={DocumentDocumentScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ManagementLogisticsScreen" component={ManagementLogisticsScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ManagementAutomationsScreen" component={ManagementAutomationsScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ManagementFinanceScreen" component={ManagementFinanceScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ManagementInvoiceEditorScreen" component={ManagementInvoiceEditorScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ManagementServiceScreen" component={ManagementServiceScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ManagementDeploymentsScreen" component={ManagementDeploymentsScreen} options={{ headerShown: false }} />
            <Stack.Screen name="HappyAiInsightsScreen" component={HappyAiInsightsScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ManagementDeploymentDetailScreen" component={ManagementDeploymentDetailScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ManagementRuntimeLogsScreen" component={ManagementRuntimeLogsScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ManagementSystemStatusScreen" component={ManagementSystemStatusScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ManagementDashboardCustomizeScreen" component={ManagementDashboardCustomizeScreen} options={{ headerShown: false }} />
            <Stack.Screen name="CalendarSettingsScreen" component={CalendarSettingsScreen} options={{ headerShown: false }} />
          </>
        )}

        {/* Repairman module screens */}
        {serviceEnabled && (
          <>
            <Stack.Screen name="RepairmanHomeScreen" component={RepairmanHomeScreen} options={getScreenOptions('RepairmanHomeScreen', { title: t('home_screen_title') })} />
            <Stack.Screen name="WorkSheetListScreen" component={WorkSheetListScreen} options={getScreenOptions('WorkSheetListScreen', { title: t('order_list_screen_title') })} />
            <Stack.Screen name="WorkSheetDetailScreen" component={WorkSheetDetailScreen} options={getScreenOptions('WorkSheetDetailScreen', { title: t('order_details_screen_title') })} />
            <Stack.Screen name="RepairmanCalendarScreen" component={RepairmanCalendarScreen} options={getScreenOptions('RepairmanCalendarScreen', { title: t('statistics_screen_title') })} />
            <Stack.Screen name="RepairPhotoScreen" component={RepairPhotoScreen} options={getScreenOptions('RepairPhotoScreen', { title: t('order_details_screen_title') })} />
            <Stack.Screen name="CustomerVehicleScreen" component={CustomerVehicleScreen} options={getScreenOptions('CustomerVehicleScreen', { title: t('order_details_screen_title') })} />
            <Stack.Screen name="PartsInventoryScreen" component={PartsInventoryScreen} options={getScreenOptions('PartsInventoryScreen', { title: t('settings_screen_title') })} />
          </>
        )}

        {/* Driver / courier (Sofor) module screens */}
        {driverEnabled && (
          <>
            <Stack.Screen name="DriverHomeScreen" component={DriverHomeScreen} options={{ headerShown: false }} />
            <Stack.Screen name="DriverRouteDetailScreen" component={DriverRouteDetailScreen} options={{ headerShown: false }} />
            <Stack.Screen name="DriverStopScreen" component={DriverStopScreen} options={{ headerShown: false }} />
            <Stack.Screen name="DriverOrderDetailScreen" component={DriverOrderDetailScreen} options={{ headerShown: false }} />
            <Stack.Screen name="DriverPartiesScreen" component={DriverPartiesScreen} options={{ headerShown: false }} />
            <Stack.Screen name="DriverPartyDetailScreen" component={DriverPartyDetailScreen} options={{ headerShown: false }} />
            <Stack.Screen name="DriverPartyCreateScreen" component={DriverPartyCreateScreen} options={{ headerShown: false }} />
            <Stack.Screen name="DriverChangeRequestsScreen" component={DriverChangeRequestsScreen} options={{ headerShown: false }} />
            <Stack.Screen name="DriverContactsScreen" component={DriverContactsScreen} options={{ headerShown: false }} />
            <Stack.Screen name="DriverBreaksScreen" component={DriverBreaksScreen} options={{ headerShown: false }} />
            <Stack.Screen name="DriverStatsScreen" component={DriverStatsScreen} options={{ headerShown: false }} />
            <Stack.Screen name="DriverWorkTimeScreen" component={DriverWorkTimeScreen} options={{ headerShown: false }} />
          </>
        )}

        {/* Warehouseman module screens */}
        {warehouseEnabled && (
          <>
            <Stack.Screen name="WarehousemanHomeScreen" component={WarehousemanHomeScreen} options={{ headerShown: false }} />
            <Stack.Screen name="WarehouseReceivingListScreen" component={WarehouseReceivingListScreen} options={{ headerShown: false }} />
            <Stack.Screen name="WarehouseReceivingScreen" component={WarehouseReceivingScreenWithProvider} options={{ headerShown: false }} />
            <Stack.Screen name="WarehouseReceivingPlacementScreen" component={WarehouseReceivingPlacementScreen} options={{ headerShown: false }} />
            <Stack.Screen name="StockCountScreen" component={StockCountScreenWithProvider} options={{ headerShown: false }} />
            <Stack.Screen name="StockDraftsScreen" component={StockDraftsScreenWithProvider} options={{ headerShown: false }} />
            <Stack.Screen name="StockWriteOffScreen" component={StockWriteOffScreenWithProvider} options={{ headerShown: false }} />
            <Stack.Screen name="WarehousePickingListScreen" component={WarehousePickingListScreen} options={{ headerShown: false }} />
            <Stack.Screen
              name="WarehousePickingScreen"
              component={WarehousePickingScreen}
              options={{ headerShown: false, gestureEnabled: false }}
            />
            <Stack.Screen name="WarehousePackingScreen" component={WarehousePackingScreen} options={{ headerShown: false }} />
            <Stack.Screen
              name="WarehousePackingCheckingScreen"
              component={WarehousePackingCheckingScreenWithProvider}
              options={{ headerShown: false, gestureEnabled: false }}
            />
          </>
        )}
      </Stack.Navigator>
      </MaybeManagementData>
    </>
  );
};

export default React.memo(AppNavigator);